import { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Table, Spinner, Badge, Modal, InputGroup } from 'react-bootstrap';
import { useApi } from '../../../contexts/ApiProvider';

import ProjectLogsModal from './ProjectLogsModal';

export default function HobbyProjects({ hobbyId }) {
    const api = useApi();

    const [projects, setProjects] = useState([]);
    const [blueprint, setBlueprint] = useState([]);
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);

    const getLocalISOString = (dateInput) => {
        const d = dateInput ? new Date(dateInput) : new Date();
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().slice(0, 16);
    };

    const [formData, setFormData] = useState({
        title: '', status: 'In Progress', start_date: getLocalISOString(), end_date: '', parameters: {}
    });

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [hobbyId]);

    const fetchData = async () => {
        setIsLoading(true);
        const [projRes, fieldsRes, itemsRes] = await Promise.all([
            api.get(`/hobbies/${hobbyId}/projects?per_page=100`),
            api.get(`/hobbies/${hobbyId}/fields`),
            api.get(`/hobbies/${hobbyId}/items?per_page=100`)
        ]);

        if (projRes.ok) setProjects(projRes.body.items || []);
        if (fieldsRes.ok) setBlueprint(fieldsRes.body.items || []);
        if (itemsRes.ok) setItems(itemsRes.body.items || []);

        setIsLoading(false);
    };

    const handleDynamicChange = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            parameters: { ...prev.parameters, [fieldName]: value }
        }));
    };

    const openCreateModal = () => {
        setEditId(null);
        setFormData({ title: '', status: 'In Progress', start_date: getLocalISOString(), end_date: '', parameters: {} });
        setShowModal(true);
    };

    const openEditModal = (proj) => {
        setEditId(proj.id);
        setFormData({
            title: proj.title,
            status: proj.status,
            start_date: proj.start_date ? getLocalISOString(proj.start_date) : '',
            end_date: proj.end_date ? getLocalISOString(proj.end_date) : '',
            parameters: proj.parameters || {}
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const res = editId
            ? await api.put(`/hobby-projects/${editId}`, formData)
            : await api.post(`/hobbies/${hobbyId}/projects`, formData);

        if (res.ok) {
            setShowModal(false);
            fetchData();
        } else {
            alert("Failed to save project.");
        }
    };

    const handleDelete = async (projId) => {
        if (!window.confirm("Delete this project? This will also remove its timeline logs.")) return;
        const res = await api.delete(`/hobby-projects/${projId}`);
        if (res.ok) fetchData();
    };

    // --- MILESTONE TIMELINE GENERATOR ---
    const getProjectMilestones = (proj) => {
        const milestones = [];

        if (proj.start_date) {
            milestones.push({ label: 'Project Started', date: new Date(proj.start_date) });
        }
        if (proj.end_date) {
            milestones.push({ label: 'Project Ended', date: new Date(proj.end_date) });
        }

        // Hunt for dynamic datetime parameters
        if (proj.parameters) {
            Object.entries(proj.parameters).forEach(([key, val]) => {
                if (!val) return;
                const fieldDef = blueprint.find(f => f.entity_type === 'Project' && f.field_name === key);
                if (fieldDef && fieldDef.data_type === 'datetime') {
                    milestones.push({ label: key, date: new Date(val) });
                }
            });
        }

        // Sort chronologically (oldest to newest)
        return milestones.sort((a, b) => a.date - b.date);
    };

    // Filter out datetimes from the standard parameter list
    const getStandardParameters = (proj) => {
        const standard = {};
        if (proj.parameters) {
            Object.entries(proj.parameters).forEach(([key, val]) => {
                const fieldDef = blueprint.find(f => f.entity_type === 'Project' && f.field_name === key);
                // If it's not a datetime, add it to the standard list
                if (!fieldDef || fieldDef.data_type !== 'datetime') {
                    standard[key] = val;
                }
            });
        }
        return standard;
    };

    const renderParameterValue = (fieldName, rawValue) => {
        if (!rawValue && rawValue !== 0 && typeof rawValue !== 'boolean') return '--';

        const fieldDef = blueprint.find(f => f.entity_type === 'Project' && f.field_name === fieldName);

        if (fieldDef && fieldDef.data_type === 'number') {
            const unit = (fieldDef.options && fieldDef.options.length > 0) ? ` ${fieldDef.options[0]}` : '';
            return `${rawValue}${unit}`;
        }

        if (fieldDef && fieldDef.data_type === 'item_reference') {
            const linkedItem = items.find(i => i.id.toString() === rawValue.toString());
            return linkedItem ? (
                <span className="text-primary fw-bold"><i className="bi bi-link-45deg me-1"></i>{linkedItem.name}</span>
            ) : <span className="text-danger">Item Not Found</span>;
        }

        if (fieldDef && fieldDef.data_type === 'multi_item_reference') {
            if (!Array.isArray(rawValue)) return rawValue.toString();
            const itemNames = rawValue.map(id => {
                const linkedItem = items.find(i => i.id.toString() === id.toString());
                return linkedItem ? linkedItem.name : 'Unknown';
            });
            return <span className="text-primary fw-bold"><i className="bi bi-link-45deg me-1"></i>{itemNames.join(', ')}</span>;
        }

        if (fieldDef && fieldDef.data_type === 'boolean') {
            return rawValue ? 'Yes' : 'No';
        }

        return rawValue.toString();
    };

    if (isLoading) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;

    return (
        <div className="fade-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold mb-0">Active Projects & Batches</h5>
                <Button variant="primary" size="sm" className="fw-bold rounded-pill px-3" onClick={openCreateModal}>
                    <i className="bi bi-plus-lg me-1"></i>Start Project
                </Button>
            </div>

            {projects.length === 0 ? (
                <div className="text-center py-5 text-muted bg-light rounded-4 border">
                    <p className="mb-0">No active projects. Start a new batch or project!</p>
                </div>
            ) : (
                <Row className="g-4">
                    {projects.map(proj => {
                        const milestones = getProjectMilestones(proj);
                        const standardParams = getStandardParameters(proj);

                        return (
                            <Col lg={6} key={proj.id}>
                                <Card className="border-0 shadow-sm rounded-4 h-100">
                                    <Card.Body className="p-4 d-flex flex-column">
                                        <div className="d-flex justify-content-between align-items-start mb-4">
                                            <h5 className="fw-bold mb-0">{proj.title}</h5>
                                            <Badge bg={proj.status === 'Completed' ? 'success' : 'primary'}>{proj.status}</Badge>
                                        </div>

                                        {/* --- THE NEW MILESTONE TIMELINE --- */}
                                        {milestones.length > 0 && (
                                            <div className="mb-4">
                                                <div className="position-relative border-start border-2 border-primary border-opacity-25 ms-2 ps-3">
                                                    {milestones.map((ms, idx) => (
                                                        <div key={idx} className="mb-3 position-relative">
                                                            <div className="position-absolute bg-primary rounded-circle" style={{ width: '10px', height: '10px', left: '-22px', top: '4px' }}></div>
                                                            <div className="small fw-bold text-dark lh-sm">{ms.label}</div>
                                                            <div className="small text-muted" style={{ fontSize: '0.75rem' }}>
                                                                {ms.date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex-grow-1">
                                            {Object.keys(standardParams).length > 0 && <h6 className="small fw-bold text-muted mb-2 text-uppercase letter-spacing-1 border-bottom pb-1">Details</h6>}
                                            {Object.entries(standardParams).map(([key, val]) => (
                                                <div key={key} className="mb-2 small d-flex justify-content-between pb-1">
                                                    <span className="text-muted">{key}:</span>
                                                    <span className="text-dark fw-medium text-end ms-3">{renderParameterValue(key, val)}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-3">
                                            <Button variant="link" className="text-decoration-none fw-bold p-0" onClick={() => setSelectedProject(proj)}>
                                                View Logs <i className="bi bi-arrow-right ms-1"></i>
                                            </Button>
                                            <div>
                                                <Button variant="link" className="text-primary p-0 me-3" onClick={() => openEditModal(proj)}>
                                                    <i className="bi bi-pencil-square hover-scale"></i>
                                                </Button>
                                                <Button variant="link" className="text-danger p-0" onClick={() => handleDelete(proj.id)}>
                                                    <i className="bi bi-trash3 hover-scale"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" fullscreen="md-down">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">{editId ? 'Edit Project' : 'Start New Project / Batch'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold text-muted mb-1">Project / Batch Title</Form.Label>
                            <Form.Control type="text" placeholder="e.g., Kombucha Batch 8" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required />
                        </Form.Group>
                        <Row className="mb-3 g-2">
                            <Col md={4}>
                                <Form.Label className="small fw-bold text-muted mb-1">Status</Form.Label>
                                <Form.Select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                                    <option value="Planned">Planned</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Completed">Completed</option>
                                </Form.Select>
                            </Col>
                            <Col md={4}>
                                <Form.Label className="small fw-bold text-muted mb-1">Start Date & Time</Form.Label>
                                <Form.Control type="datetime-local" value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} required />
                            </Col>
                            <Col md={4}>
                                <Form.Label className="small fw-bold text-muted mb-1">End Date & Time</Form.Label>
                                <Form.Control type="datetime-local" value={formData.end_date} onChange={e => setFormData({...formData, end_date: e.target.value})} />
                            </Col>
                        </Row>

                        {blueprint.filter(f => f.entity_type === 'Project').length > 0 && <h6 className="fw-bold mt-4 mb-3 border-bottom pb-2">Custom Parameters</h6>}

                        {blueprint.filter(f => f.entity_type === 'Project').map(field => (
                            <Form.Group key={field.id} className="mb-3">
                                <Form.Label className="small fw-bold text-primary mb-1">{field.field_name}</Form.Label>

                                {field.data_type === 'text' && (
                                    <Form.Control type="text" value={formData.parameters[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)} />
                                )}

                                {field.data_type === 'datetime' && (
                                    <Form.Control type="datetime-local" value={formData.parameters[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)} />
                                )}

                                {field.data_type === 'number' && (
                                    <InputGroup>
                                        <Form.Control type="number" step="0.1" value={formData.parameters[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)} />
                                        {field.options && field.options.length > 0 && (
                                            <InputGroup.Text className="bg-light text-muted small">{field.options[0]}</InputGroup.Text>
                                        )}
                                    </InputGroup>
                                )}

                                {field.data_type === 'dropdown' && (
                                    <Form.Select value={formData.parameters[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)}>
                                        <option value="">Select...</option>
                                        {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                    </Form.Select>
                                )}

                                {field.data_type === 'boolean' && (
                                    <Form.Check type="switch" checked={formData.parameters[field.field_name] || false} onChange={e => handleDynamicChange(field.field_name, e.target.checked)} />
                                )}

                                {field.data_type === 'item_reference' && (
                                    <Form.Select value={formData.parameters[field.field_name] || ''} onChange={e => handleDynamicChange(field.field_name, e.target.value)}>
                                        <option value="">Select an item...</option>
                                        {items.map(item => (
                                            <option key={item.id} value={item.id}>
                                                {item.name} {item.category ? `(${item.category})` : ''}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}

                                {field.data_type === 'multi_item_reference' && (() => {
                                    let availableItems = items;
                                    if (field.options && field.options.length > 0) {
                                        availableItems = items.filter(item =>
                                            item.category && field.options.includes(item.category.trim())
                                        );
                                    }
                                    return (
                                        <>
                                            <Form.Select
                                                multiple
                                                value={formData.parameters[field.field_name] || []}
                                                onChange={e => {
                                                    const selectedOpts = Array.from(e.target.selectedOptions, option => option.value);
                                                    handleDynamicChange(field.field_name, selectedOpts);
                                                }}
                                            >
                                                {availableItems.map(item => (
                                                    <option key={item.id} value={item.id}>
                                                        {item.name} {item.category ? `(${item.category})` : ''}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Text className="text-muted small">Hold Ctrl (Windows) or Cmd (Mac) to select multiple.</Form.Text>
                                        </>
                                    );
                                })()}
                            </Form.Group>
                        ))}

                        <Button variant="primary" type="submit" className="w-100 fw-bold rounded-pill mt-3">Save Project</Button>
                    </Form>
                </Modal.Body>
            </Modal>

            <ProjectLogsModal
                show={selectedProject !== null}
                onHide={() => setSelectedProject(null)}
                project={selectedProject}
                blueprint={blueprint.filter(f => f.entity_type === 'Log')}
                items={items}
            />
        </div>
    );
}
